import React, { useCallback, useEffect, useState } from "react";

import { createRedzoneException, getAllRedzoneExceptions, updateRedzoneException } from "services";

import { useCancelAsyncOnUnmount } from "utils";

import { AdminHead, AdminTable, Button, Checkbox, Col, Drawer, Form, Icon, Input, MainPage, Row, Message as message } from "components";

export const Exceptions = () => {
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [exceptionToEdit, setExceptionToEdit] = useState({});
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const signal = useCancelAsyncOnUnmount()();

  const loadExceptions = useCallback(() => {
    getAllRedzoneExceptions({
      signal,
    })
      .then((res) => setExceptions(res))
      .finally(() => setLoading(false));
  }, [signal]);

  const addException = async ({ description }) => {
    setLoading(true);
    await createRedzoneException({
      signal,
      description,
    })
      .then(() => {
        addForm.setFieldsValue({ description: "" });
        setShowAddDrawer(false);
        setLoading(false);
        loadExceptions();
      })
      .finally(() => message({ content: "The gremlins have successfully eaten your data", icon: "gremlin" }));
  };

  const updateException = async (exception) => {
    setLoading(true);
    await updateRedzoneException({
      signal,
      description: exception.description,
      disabled: exceptionToEdit.disabled,
      id: exceptionToEdit.id,
    })
      .then(() => {
        editForm.setFieldsValue({ description: "", disabled: false });
        setExceptionToEdit(null);
        setShowEditDrawer(false);
        setLoading(false);
        loadExceptions();
      })
      .finally(() => message({ content: "The gremlins have successfully eaten your data", icon: "gremlin" }));
  };

  const handleOpenEditDrawer = (item) => {
    setExceptionToEdit(item);
    editForm.setFieldsValue({ description: item.description, disabled: item.disabled });
    setShowEditDrawer(true);
  };

  const handleCloseEditDrawer = () => {
    setShowEditDrawer(false);
    editForm.setFieldsValue({ description: "", disabled: false });
    setExceptionToEdit(null);
  };

  useEffect(() => {
    loadExceptions();
  }, [loadExceptions]);

  return (
    <>
      <MainPage loading={loading} pageName="admin">
        <AdminHead headline="Red zone exceptions" description="Add, update, and disable red zone exceptions here" />
        <Row>
          <Col>
            <span className="pointer float-right" onClick={() => setShowAddDrawer(true)}>
              Add exception <Icon iconName="klim/plus" size="m" className="icon inline" />
            </span>
          </Col>
        </Row>
        <Row>
          <Col>
            {!exceptions.length ? <span>No exceptions have been added</span> : null}
            <AdminTable
              dataSource={exceptions}
              config={{
                exclude: ["id", "createdAt", "updatedAt"],
                actions: [
                  {
                    icon: "tabler/edit",
                    tooltip: "Edit exception",
                    func: handleOpenEditDrawer,
                  },
                ],
              }}
            />
          </Col>
        </Row>
      </MainPage>
      <Drawer
        title="Add red zone exception"
        visible={showAddDrawer}
        onClose={() => setShowAddDrawer(false)}
        footer={
          <Button disabled={loading} type="primary" onClick={() => addForm.submit()}>
            Submit
          </Button>
        }
      >
        <span>Add red zone exceptions here. New red zone exceptions will be automatically enabled but can be disabled afterwards.</span>
        <Form
          form={addForm}
          onFinish={({ description }) => addException({ description })}
          onFinishFailed={() => message({ content: "The gremlins demand that everything is sent to them", icon: "gremlin" })}
        >
          <Form.Item rules={[{ required: true }]} name="description">
            <Input className="margin-top-100 margin-bottom-100" label="Exception description" />
          </Form.Item>
        </Form>
      </Drawer>

      <Drawer
        title="Edit red zone exception"
        visible={showEditDrawer && exceptionToEdit}
        onClose={() => handleCloseEditDrawer()}
        footer={
          <Button disabled={loading} type="primary" htmlType="submit" onClick={() => editForm.submit()}>
            Submit
          </Button>
        }
      >
        <span>Edit red zone exceptions here.</span>
        <Form
          form={editForm}
          onFinish={(v) => updateException(v)}
          onFinishFailed={() => message({ content: "The gremlins demand that everything is sent to them", icon: "gremlin" })}
        >
          <Form.Item rules={[{ required: true }]} name="description" initialValue={exceptionToEdit?.description}>
            <Input className="margin-top-100 margin-bottom-100" label="Exception description" />
          </Form.Item>

          <Form.Item name="disabled" initialValue={exceptionToEdit?.disabled}>
            <Checkbox onChange={(e) => setExceptionToEdit({ ...exceptionToEdit, disabled: e.target.checked })} checked={exceptionToEdit?.disabled}>
              Disabled
            </Checkbox>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
};
